// ─── Currency ────────────────────────────────────────────────────────────────

export function formatDollars(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(cents / 100);
}

// ─── Rarity ──────────────────────────────────────────────────────────────────

const RARITY_COLOR_MAP: Record<string, string> = {
  'Consumer Grade':   'text-slate-400',
  'Industrial Grade': 'text-sky-400',
  'Mil-Spec Grade':   'text-blue-500',
  'Restricted':       'text-purple-500',
  'Classified':       'text-pink-500',
  'Covert':           'text-red-500',
  'Contraband':       'text-amber-500',
  'Base Grade':       'text-slate-400',
  'High Grade':       'text-sky-400',
  'Remarkable':       'text-purple-500',
  'Extraordinary':    'text-red-500',
};

export function rarityColor(rarity: string): string {
  return RARITY_COLOR_MAP[rarity] ?? 'text-slate-400';
}

const RARITY_DOT_MAP: Record<string, string> = {
  'Consumer Grade':   'bg-slate-400',
  'Industrial Grade': 'bg-sky-400',
  'Mil-Spec Grade':   'bg-blue-500',
  'Restricted':       'bg-purple-500',
  'Classified':       'bg-pink-500',
  'Covert':           'bg-red-500',
  'Contraband':       'bg-amber-500',
  'Base Grade':       'bg-slate-400',
  'High Grade':       'bg-sky-400',
  'Remarkable':       'bg-purple-500',
  'Extraordinary':    'bg-red-500',
};

export function rarityDotColor(rarity: string): string {
  return RARITY_DOT_MAP[rarity] ?? 'bg-slate-400';
}

// ─── Wear ─────────────────────────────────────────────────────────────────────

const WEAR_MAP: Record<string, string> = {
  'Factory New':        'FN',
  'Minimal Wear':       'MW',
  'Field-Tested':       'FT',
  'Well-Worn':          'WW',
  'Battle-Scarred':     'BS',
};

export function wearAbbrev(wear: string): string {
  return WEAR_MAP[wear] ?? wear;
}

// ─── Order status ─────────────────────────────────────────────────────────────

export interface StatusBadgeInfo {
  label: string;
  class: string;
}

// Epic Cherry status palette — dark-theme pills built from app.css tokens.
const STATUS_IN_PROGRESS = 'bg-[var(--color-accent-dim)] text-[var(--color-accent)] border border-[var(--color-accent-ring)]';
const STATUS_SUCCESS = 'bg-[var(--color-success-dim)] text-[var(--color-success)] border border-[color-mix(in_srgb,var(--color-success)_30%,transparent)]';
const STATUS_ERROR = 'bg-[var(--color-error-dim)] text-[var(--color-error)] border border-[color-mix(in_srgb,var(--color-error)_30%,transparent)]';
const STATUS_WARNING = 'bg-[var(--color-warning-dim)] text-[var(--color-warning)] border border-[color-mix(in_srgb,var(--color-warning)_30%,transparent)]';
const STATUS_MUTED = 'bg-[var(--color-surface-3)] text-[var(--color-text-muted)] border border-[var(--color-border-medium)]';

const STATUS_MAP: Record<string, StatusBadgeInfo> = {
  pending:     { label: 'Pending',     class: STATUS_WARNING },
  processing:  { label: 'Processing',  class: STATUS_IN_PROGRESS },
  purchasing:  { label: 'Purchasing',  class: STATUS_IN_PROGRESS },
  purchased:   { label: 'Purchased',   class: STATUS_IN_PROGRESS },
  withdrawing: { label: 'Withdrawing', class: STATUS_IN_PROGRESS },
  trade_sent:  { label: 'Trade Sent',  class: STATUS_SUCCESS },
  delivered:   { label: 'Delivered',   class: STATUS_SUCCESS },
  completed:   { label: 'Completed',   class: STATUS_SUCCESS },
  failed:      { label: 'Failed',      class: STATUS_ERROR },
  refunded:    { label: 'Refunded',    class: STATUS_ERROR },
  cancelled:   { label: 'Cancelled',   class: STATUS_MUTED },
};

export function statusBadge(status: string): StatusBadgeInfo {
  return STATUS_MAP[status.toLowerCase()] ?? {
    label: status,
    class: STATUS_MUTED,
  };
}

export const TERMINAL_STATUSES = new Set([
  'completed',
  'delivered',
  'failed',
  'refunded',
  'cancelled',
]);

// ─── Date / time ──────────────────────────────────────────────────────────────

export function formatDate(iso: string): string {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}
